import styled from 'styled-components'

import { Typography } from 'components/Typography/Typography'
import { useIsMobile } from 'utils/useIsMobile'

const SliderContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`

interface StyledInputProps {
  value: number
  min: number
  max: number
}

const DisabledSlider = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 5px;
  background-color: ${p => p.theme.colors.disabledSlider};
`

const StyledInput = styled.input.attrs<StyledInputProps>(({ theme, value, min, max }) => ({
  style: {
    background: `linear-gradient(
      to right, 
      ${theme.colors.text.active} 0%, 
      ${theme.colors.text.active} ${((value - min) / (max - min)) * 100}%, 
      ${theme.colors.text.activeTransparent} ${((value - min) / (max - min)) * 100}%, 
      ${theme.colors.text.activeTransparent} 100%)`,
  },
}))<StyledInputProps>`
  width: 100%;
  appearance: none;
  height: 8px;
  border-radius: 5px;
  outline: none;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 14px;
    height: 14px;
    background: ${p => p.theme.colors.text.active};
    border-radius: 50%;
    cursor: pointer;
    position: relative;
    z-index: 1;
  }

  &::-moz-range-thumb {
    width: 14px;
    height: 14px;
    background: ${p => p.theme.colors.text.active};
    border-radius: 50%;
    cursor: pointer;
    position: relative;
    z-index: 1;
  }

  &::-ms-thumb {
    width: 14px;
    height: 14px;
    background: ${p => p.theme.colors.text.active};
    border-radius: 50%;
    cursor: pointer;
    position: relative;
    z-index: 1;
  }
`

const LabelContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
`

interface SliderComponentProps {
  hideLabels?: boolean
  labels?: number[]
  disabled?: boolean
  min: number
  max?: number
  step: number
  value: number
  onChange: (value: number) => void
}

const Slider: React.FC<SliderComponentProps> = ({ min, max, step, onChange, hideLabels, value, labels, disabled }) => {
  const isMobile = useIsMobile()

  const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseFloat(event.target.value)
    onChange(newValue)
  }

  if (disabled) {
    return <DisabledSlider />
  }

  if (!max) {
    return null
  }

  return (
    <SliderContainer>
      <StyledInput type="range" min={min} max={max} step={step} value={value} onChange={handleSliderChange} />
      {!hideLabels && labels?.length && (
        <LabelContainer>
          {labels.map(l => (
            <div key={l} style={{ cursor: 'pointer' }} onClick={() => onChange(l)}>
              <Typography fontWeight="bold" variant={isMobile ? 'caption' : 'description'}>
                {l}x
              </Typography>
            </div>
          ))}
        </LabelContainer>
      )}
    </SliderContainer>
  )
}

export default Slider
